import { useEffect } from "react";
import {
  useAppSelector,
  resetStore,
  setSyncOwner,
  setOnline,
  subscribeRealtime,
} from "@reciperoll/core";
import { store, persistor } from "../store/store";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { runSync } from "./syncManager";

// Fallback poll — Realtime is the primary low-latency path; this catches changes
// missed while the socket was down and tables not subscribed to Realtime.
const POLL_MS = 5 * 60_000;

// Mount once inside the Redux <Provider> and <AuthProvider> to drive web sync:
// connectivity tracking, login/foreground/poll triggers, and Realtime.
export function SyncRoot() {
  const { session, loading } = useAuth();
  const userId = session?.user.id ?? null;
  const accessToken = session?.access_token ?? null;
  const isOnline = useAppSelector((s) => s.sync.isOnline);

  // Track connectivity in the sync slice; kick a catch-up sync on reconnect.
  useEffect(() => {
    const update = () => {
      const online = typeof navigator === "undefined" ? true : navigator.onLine;
      const was = store.getState().sync.isOnline;
      store.dispatch(setOnline(online));
      if (online && !was) runSync(); // regained connectivity
    };
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  // Login / tab-focus / interval-fallback triggers, plus per-user reset.
  useEffect(() => {
    if (loading || !userId) return; // wait for auth; nothing while signed out

    let cancelled = false;
    (async () => {
      // Different user on this browser → wipe the previous user's local data
      // before pulling this user's. (Same user → keep cache + unpushed edits.)
      const owner = store.getState().sync.ownerUserId;
      if (owner && owner !== userId) {
        store.dispatch(resetStore());
        await persistor.purge();
      }
      if (cancelled) return;
      store.dispatch(setSyncOwner(userId));
      runSync(); // initial sync on login / load while authed
    })();

    const interval = setInterval(runSync, POLL_MS); // fallback poll
    const onVisible = () => {
      if (document.visibilityState === "visible") runSync(); // pull on tab focus
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loading, userId]);

  // Low-latency Realtime notifications of remote changes.
  useEffect(() => {
    if (loading || !userId || !accessToken || !isOnline) return; // authed + online
    return subscribeRealtime({ supabase, store, userId, accessToken, runSync });
  }, [loading, userId, accessToken, isOnline]);

  return null;
}
